import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../api/base';
import { getLocalized } from '../utils/localized';

interface SeedlingType {
  name: { ru?: string; ky?: string; en?: string };
  count?: number;
}

interface EventLocation {
  id: number;
  name: { ru?: string; ky?: string; en?: string };
  latitude: number | null;
  longitude: number | null;
  regionId: number | null;
}

interface Festival {
  id: number;
  slug: string;
  name: { ru?: string; ky?: string; en?: string };
  locationName: string | null;
  locations?: EventLocation[];
  eventDate: string;
  status: string;
  participantsCount: number | null;
  areaHa: number | null;
  seedlingTypes: SeedlingType[];
  targetTrees: number | null;
}

interface EventMediaItem {
  id: number;
  type: 'image' | 'video';
  url: string;
  thumbnail: string | null;
  caption: string | { ru?: string; ky?: string; en?: string } | null;
}

interface FestivalDetail extends Festival {
  stats: { totalTrees: number; totalAmount: number; totalDonors: number };
  companies: any[];
  media?: EventMediaItem[];
}

interface RegionInfo {
  id: number;
  name: { ru?: string; ky?: string; en?: string };
}

const FestivalsSection: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [festivals, setFestivals] = useState<Festival[]>([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [detail, setDetail] = useState<FestivalDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const listRef = useRef<HTMLDivElement | null>(null);
  const [canUp, setCanUp] = useState(false);
  const [canDown, setCanDown] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);
  const [regionsById, setRegionsById] = useState<Map<number, RegionInfo>>(new Map());
  const [locationsPopoverOpen, setLocationsPopoverOpen] = useState(false);
  const popoverTriggerRef = useRef<HTMLSpanElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get<{ regions: RegionInfo[] }>(`${API_URL}/api/regions`);
        const map = new Map<number, RegionInfo>();
        (data.regions || []).forEach(r => map.set(r.id, r));
        setRegionsById(map);
      } catch (err) {
        console.error('regions load err', err);
      }
    })();
  }, []);

  // закрытие popover по клику вне и по Esc
  useEffect(() => {
    if (!locationsPopoverOpen) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (popoverRef.current?.contains(t)) return;
      if (popoverTriggerRef.current?.contains(t)) return;
      setLocationsPopoverOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLocationsPopoverOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [locationsPopoverOpen]);

  // при смене активного фестиваля — закрыть popover
  useEffect(() => {
    setLocationsPopoverOpen(false);
  }, [activeIdx]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get<{ events: Festival[] }>(`${API_URL}/api/events`);
        // upcoming first (future & not finished), then past new→old
        const now = Date.now();
        const upcoming = (f: Festival) => {
          const t = new Date(f.eventDate.replace('Z', '')).getTime();
          if (t < now) return false;
          return f.status === 'planned' || f.status === 'active';
        };
        const sorted = [...data.events].sort((a, b) => {
          const au = upcoming(a) ? 0 : 1;
          const bu = upcoming(b) ? 0 : 1;
          if (au !== bu) return au - bu;
          return new Date(b.eventDate).getTime() - new Date(a.eventDate).getTime();
        });
        setFestivals(sorted);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const active = festivals[activeIdx];

  useEffect(() => {
    if (!active) return;
    setDetail(null);
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/events/${active.slug}`);
        setDetail({ ...data.event, stats: data.stats, companies: data.companies || [], media: data.media || [] });
      } catch (err) {
        console.error(err);
      }
    })();
  }, [active]);

  // Внешние компоненты (карта) могут открывать конкретный фестиваль
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (!detail?.slug) return;
      const idx = festivals.findIndex(f => f.slug === detail.slug);
      if (idx >= 0) setActiveIdx(idx);
    };
    window.addEventListener('festival:activate', handler);
    return () => window.removeEventListener('festival:activate', handler);
  }, [festivals]);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const upd = () => {
      setCanUp(el.scrollTop > 2);
      setCanDown(el.scrollTop + el.clientHeight < el.scrollHeight - 2);
    };
    upd();
    el.addEventListener('scroll', upd);
    const ro = new ResizeObserver(upd);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', upd);
      ro.disconnect();
    };
  }, [festivals]);

  // Прижимаем активный chip к началу видимой области списка.
  // offsetTop может считаться от body (если listRef не позиционирован),
  // поэтому используем разницу getBoundingClientRect.
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const active = el.querySelector<HTMLElement>(`[data-fest-idx='${activeIdx}']`);
    if (!active) return;
    const activeRect = active.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    const isHorizontal = el.scrollWidth > el.clientWidth;
    if (isHorizontal) {
      const delta = activeRect.left - elRect.left;
      const max = el.scrollWidth - el.clientWidth;
      el.scrollTo({ left: Math.max(0, Math.min(el.scrollLeft + delta - 6, max)), behavior: 'smooth' });
    } else {
      const delta = activeRect.top - elRect.top;
      const max = el.scrollHeight - el.clientHeight;
      el.scrollTo({ top: Math.max(0, Math.min(el.scrollTop + delta - 6, max)), behavior: 'smooth' });
    }
  }, [activeIdx, festivals]);

  if (loading) return null;
  if (festivals.length === 0) return null;

  const scrollList = (dir: -1 | 1) => {
    const el = listRef.current;
    if (!el) return;
    el.scrollBy({ top: dir * Math.max(60, el.clientHeight * 0.7), behavior: 'smooth' });
  };

  const isUpcoming = (f: Festival) => {
    // если дата в прошлом — фестиваль прошёл, даже если админ не поменял status
    const eventTime = new Date(f.eventDate.replace('Z', '')).getTime();
    if (eventTime < Date.now()) return false;
    return f.status === 'planned' || f.status === 'active';
  };

  const fmtDateWithMonth = (s: string) =>
    new Date(s.replace('Z', '')).toLocaleDateString(i18n.language === 'en' ? 'en-GB' : 'ru-RU', {
      month: 'long',
      year: 'numeric'
    });

  const fmtFullDate = (s: string) =>
    new Date(s.replace('Z', '')).toLocaleDateString(i18n.language === 'en' ? 'en-GB' : 'ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });

  return (
    <section id="festivals" style={{ background: '#fff', padding: '64px 24px' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 14 }}>
          <span className="section-tag">{t('festivals.tag', 'Дважды в год')}</span>
        </div>
        <h2 className="h2-center" style={{ marginBottom: 30 }}>
          {t('festivals.title')}
        </h2>

        <div className="grid-festivals">
          {/* LEFT LIST */}
          <div className="fest-list-col" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <NavButton dir="up" disabled={!canUp} onClick={() => scrollList(-1)} />
            <div
              ref={listRef}
              className="scrollbar-hide fest-list"
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
                maxHeight: 300,
                overflowY: 'auto',
                padding: '4px 4px'
              }}
            >
              {festivals.map((f, i) => {
                const on = i === activeIdx;
                const up = isUpcoming(f);
                return (
                  <button
                    key={f.slug}
                    type="button"
                    data-fest-idx={i}
                    onClick={() => setActiveIdx(i)}
                    className={`fest-chip ${on ? 'active' : ''}`}
                    style={{
                      padding: '16px 18px',
                      cursor: 'pointer',
                      transition: 'border-color 150ms, opacity 150ms',
                      boxShadow: 'none',
                      border: on ? '2px solid #5EA82E' : '1.5px solid #DCE8CF',
                      borderRadius: 22,
                      opacity: on ? 1 : 0.85,
                      textAlign: 'left',
                      background: '#fff'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                      <span style={{ font: "600 15px 'Montserrat Alternates',sans-serif", color: '#2D5016' }}>
                        {getLocalized(f.name, i18n.language)}
                      </span>
                      <Badge upcoming={up} small />
                    </div>
                    <div style={{ font: "400 12.5px 'Golos Text',sans-serif", color: '#777', marginTop: 4 }}>
                      {fmtDateWithMonth(f.eventDate)}
                    </div>
                  </button>
                );
              })}
            </div>
            <NavButton dir="down" disabled={!canDown} onClick={() => scrollList(1)} />
          </div>

          {/* RIGHT DETAIL */}
          <div className="card-base" style={{ padding: '32px 36px' }}>
            {!detail ? (
              <div style={{ textAlign: 'center', color: '#9aa88c', padding: '40px 0' }}>{t('loading')}</div>
            ) : (
              <>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    flexWrap: 'wrap',
                    gap: 10
                  }}
                >
                  <div>
                    <div id="fest-detail-name" style={{ font: "700 26px 'Montserrat Alternates',sans-serif", color: '#2D5016' }}>
                      {getLocalized(detail.name, i18n.language)}
                    </div>
                    <div style={{ font: "400 15px 'Golos Text',sans-serif", color: '#777', marginTop: 6, position: 'relative' }}>
                      {(() => {
                        const locs = detail.locations || [];
                        const first = locs[0]
                          ? getLocalized(locs[0].name, i18n.language)
                          : detail.locationName;
                        const extra = Math.max(0, locs.length - 1);
                        const firstRegion = locs[0]?.regionId != null
                          ? regionsById.get(locs[0].regionId)
                          : null;

                        return (
                          <>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                              <span>{first || '—'}</span>
                              {firstRegion && (
                                <span style={{
                                  font: "500 12.5px 'Golos Text',sans-serif",
                                  color: '#5a6b4d',
                                  background: '#eff5e6',
                                  borderRadius: 999,
                                  padding: '2px 10px',
                                  whiteSpace: 'nowrap',
                                  lineHeight: 1.5
                                }}>
                                  {getLocalized(firstRegion.name, i18n.language)}
                                </span>
                              )}
                            </span>
                            {extra > 0 && (
                              <>
                                {' · '}
                                <span
                                  ref={popoverTriggerRef}
                                  role="button"
                                  tabIndex={0}
                                  onClick={() => setLocationsPopoverOpen(o => !o)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      setLocationsPopoverOpen(o => !o);
                                    }
                                  }}
                                  style={{
                                    color: '#0B7A43',
                                    cursor: 'pointer',
                                    textDecoration: 'underline',
                                    textDecorationStyle: 'dotted',
                                    textUnderlineOffset: 3
                                  }}
                                >
                                  {t('festivals.and_more_places', { count: extra })}
                                </span>
                              </>
                            )}
                            {' · '}{fmtFullDate(detail.eventDate)}

                            {locationsPopoverOpen && extra > 0 && (
                              <div
                                ref={popoverRef}
                                style={{
                                  position: 'absolute',
                                  top: '100%',
                                  left: 0,
                                  marginTop: 10,
                                  minWidth: 300,
                                  maxWidth: 420,
                                  background: '#fff',
                                  borderRadius: 14,
                                  boxShadow: '0 12px 32px rgba(0,20,8,.22)',
                                  padding: '14px 16px',
                                  zIndex: 30
                                }}
                              >
                                <div style={{
                                  font: "700 13px 'Golos Text',sans-serif",
                                  color: '#2D5016',
                                  marginBottom: 10,
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center'
                                }}>
                                  <span>{t('festivals.locations_title', 'Локации фестиваля')}</span>
                                  <button
                                    type="button"
                                    onClick={() => setLocationsPopoverOpen(false)}
                                    aria-label="close"
                                    style={{
                                      border: 0,
                                      background: 'transparent',
                                      color: '#777',
                                      cursor: 'pointer',
                                      font: "400 18px 'Golos Text',sans-serif",
                                      lineHeight: 1,
                                      padding: 0
                                    }}
                                  >×</button>
                                </div>
                                <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                                  {locs.map(loc => {
                                    const label = getLocalized(loc.name, i18n.language) || '—';
                                    const region = loc.regionId != null
                                      ? regionsById.get(loc.regionId)
                                      : null;
                                    const regionLabel = region
                                      ? getLocalized(region.name, i18n.language)
                                      : t('festivals.no_region', 'Кыргызстан');
                                    return (
                                      <li
                                        key={loc.id}
                                        style={{
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          alignItems: 'center',
                                          gap: 12,
                                          padding: '7px 0',
                                          borderTop: '1px solid #eef2ea',
                                          font: "400 14px 'Golos Text',sans-serif",
                                          color: '#3a4a30'
                                        }}
                                      >
                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                          <span aria-hidden>📍</span>
                                          {label}
                                        </span>
                                        <span style={{
                                          font: "500 12.5px 'Golos Text',sans-serif",
                                          color: '#5a6b4d',
                                          background: '#eff5e6',
                                          borderRadius: 999,
                                          padding: '3px 10px',
                                          whiteSpace: 'nowrap'
                                        }}>{regionLabel}</span>
                                      </li>
                                    );
                                  })}
                                </ul>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                  <Badge upcoming={isUpcoming(detail)} />
                </div>

                <div className="grid-3" style={{ gap: 14, margin: '24px 0' }}>
                  <FestStat
                    value={
                      isUpcoming(detail)
                        ? (detail.targetTrees ?? 0).toLocaleString('ru-RU')
                        : detail.stats.totalTrees.toLocaleString('ru-RU')
                    }
                    label={t('festivals.trees', 'саженцев')}
                  />
                  <FestStat
                    value={
                      detail.participantsCount != null
                        ? detail.participantsCount.toLocaleString('ru-RU')
                        : '—'
                    }
                    label={t('festivals.participants', 'участников')}
                  />
                  <FestStat
                    value={String(detail.companies.length)}
                    label={t('festivals.partners_count', 'партнёров')}
                  />
                </div>

                {detail.seedlingTypes?.length > 0 && (
                  <>
                    <div style={{ font: "600 14px 'Golos Text',sans-serif", color: '#445', marginBottom: 9 }}>
                      {t('festivals.seedling_types', 'Виды саженцев')}:
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {detail.seedlingTypes.map((s, i) => (
                        <span
                          key={i}
                          style={{
                            background: '#DFEFCB',
                            color: '#0B7A43',
                            borderRadius: 999,
                            padding: '7px 15px',
                            font: "500 13.5px 'Golos Text',sans-serif"
                          }}
                        >
                          {getLocalized(s.name, i18n.language)}
                        </span>
                      ))}
                    </div>
                  </>
                )}

                {(detail.media?.filter(m => m.type === 'image').length ?? 0) > 0 && (
                  <div style={{ marginTop: 24 }}>
                    <div style={{ font: "600 14px 'Golos Text',sans-serif", color: '#445', marginBottom: 10 }}>
                      {t('festivals.photos', 'Фото с фестиваля')}
                    </div>
                    <div
                      className="festival-gallery"
                      style={{
                        display: 'flex',
                        gap: 10,
                        overflowX: 'auto',
                        paddingBottom: 6,
                        scrollbarWidth: 'thin'
                      }}
                    >
                      {detail.media!.filter(m => m.type === 'image').map((m, i) => (
                        <button
                          key={m.id}
                          type="button"
                          onClick={() => setLightboxIdx(i)}
                          className="festival-gallery-item"
                          style={{
                            flex: '0 0 auto',
                            width: 140,
                            height: 100,
                            borderRadius: 12,
                            overflow: 'hidden',
                            border: 0,
                            padding: 0,
                            cursor: 'pointer',
                            background: '#DFEFCB'
                          }}
                        >
                          <img
                            src={`${API_URL}${m.url}`}
                            alt=""
                            loading="lazy"
                            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div style={{ display: 'flex', gap: 24, marginTop: 24, flexWrap: 'wrap' }}>
                  <a
                    href="#partners-rating"
                    style={{ font: "600 15px 'Golos Text',sans-serif" }}
                  >
                    {t('festivals.partners_link', 'Партнёры фестиваля')} →
                  </a>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {lightboxIdx !== null && detail?.media && (
        <Lightbox
          images={detail.media.filter(m => m.type === 'image').map(m => `${API_URL}${m.url}`)}
          index={lightboxIdx}
          onClose={() => setLightboxIdx(null)}
          onChange={setLightboxIdx}
        />
      )}
    </section>
  );
};

const Lightbox: React.FC<{
  images: string[];
  index: number;
  onClose: () => void;
  onChange: (idx: number) => void;
}> = ({ images, index, onClose, onChange }) => {
  const total = images.length;
  const prev = () => onChange((index - 1 + total) % total);
  const next = () => onChange((index + 1) % total);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') prev();
      else if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [index, total]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.85)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24
      }}
    >
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        aria-label="Закрыть"
        style={{
          position: 'absolute',
          top: 20,
          right: 20,
          width: 44,
          height: 44,
          borderRadius: '50%',
          border: 0,
          background: 'rgba(255,255,255,0.15)',
          color: '#fff',
          fontSize: 24,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        ×
      </button>

      {total > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); prev(); }}
            aria-label="Назад"
            style={{
              position: 'absolute',
              left: 20,
              top: '50%',
              transform: 'translateY(-50%)',
              width: 52,
              height: 52,
              borderRadius: '50%',
              border: 0,
              background: 'rgba(255,255,255,0.15)',
              color: '#fff',
              fontSize: 28,
              cursor: 'pointer'
            }}
          >
            ‹
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); next(); }}
            aria-label="Вперёд"
            style={{
              position: 'absolute',
              right: 20,
              top: '50%',
              transform: 'translateY(-50%)',
              width: 52,
              height: 52,
              borderRadius: '50%',
              border: 0,
              background: 'rgba(255,255,255,0.15)',
              color: '#fff',
              fontSize: 28,
              cursor: 'pointer'
            }}
          >
            ›
          </button>
        </>
      )}

      <img
        src={images[index]}
        alt=""
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: '92vw',
          maxHeight: '88vh',
          objectFit: 'contain',
          borderRadius: 8,
          boxShadow: '0 10px 40px rgba(0,0,0,0.5)'
        }}
      />

      {total > 1 && (
        <div
          style={{
            position: 'absolute',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            color: 'rgba(255,255,255,0.75)',
            font: "500 13px 'Golos Text',sans-serif"
          }}
        >
          {index + 1} / {total}
        </div>
      )}
    </div>
  );
};

const Badge: React.FC<{ upcoming: boolean; small?: boolean }> = ({ upcoming, small }) =>
  upcoming ? (
    <span
      style={{
        background: '#E87A16',
        color: '#fff',
        borderRadius: 999,
        padding: small ? '4px 11px' : '6px 14px',
        font: `600 ${small ? 11 : 12}px 'Golos Text',sans-serif`
      }}
    >
      {small ? 'скоро' : 'Предстоит'}
    </span>
  ) : (
    <span
      style={{
        background: '#EEF4E6',
        color: '#2D5016',
        borderRadius: 999,
        padding: small ? '4px 11px' : '6px 14px',
        font: `600 ${small ? 11 : 12}px 'Golos Text',sans-serif`,
        boxShadow: 'inset 0 0 0 1px #CFE6B4'
      }}
    >
      прошёл
    </span>
  );

const NavButton: React.FC<{ dir: 'up' | 'down'; disabled: boolean; onClick: () => void }> = ({
  dir,
  disabled,
  onClick
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label={dir === 'up' ? 'Вверх' : 'Вниз'}
    style={{
      border: 0,
      borderRadius: 14,
      background: '#fff',
      boxShadow: '0 4px 14px rgba(45,80,22,.1), inset 0 0 0 1.5px #CFE6B4',
      color: '#0B7A43',
      fontSize: 13,
      padding: '9px 0',
      cursor: disabled ? 'default' : 'pointer',
      opacity: disabled ? 0.4 : 1,
      transition: 'background 150ms'
    }}
  >
    {dir === 'up' ? '▲' : '▼'}
  </button>
);

const FestStat: React.FC<{ value: string; label: string }> = ({ value, label }) => (
  <div style={{ background: '#DFEFCB', borderRadius: 16, padding: '14px 20px' }}>
    <div style={{ font: "700 24px 'Montserrat Alternates',sans-serif", color: '#0B7A43' }}>{value}</div>
    <div style={{ font: "400 13px 'Golos Text',sans-serif", color: '#5a6b4d', marginTop: 2 }}>{label}</div>
  </div>
);

export default FestivalsSection;
